import { ConnectorHosting } from "@p0security/integrations-app-shared/components";
import {
  newVaultElement,
  withoutAzureKeyVault,
} from "@p0security/integrations-vaulted-credential-shared";
import {
  Element,
  InstallSpec,
  ItemComponent,
} from "@p0security/shared/install/types";

/** Docker Hub's rule for an organization's name, which is also its namespace.
 * It's the connector's `org` schema (p0-connector-docker), and the connector
 * refuses any other name. */
export const DOCKER_ORG_PATTERN = /^[a-z0-9][a-z0-9_-]{1,254}$/;

export const INVALID_ORG_NAME =
  "Enter the organization's name as Docker Hub shows it: 2 to 255 lowercase letters, digits, hyphens and underscores, starting with a letter or digit.";

// A connector's id names its Terraform resources, and each organization picks
// its connector by it. The pattern and its 63-character limit are a DNS
// label's (RFC 1123), as Kubernetes names an object.
const CONNECTOR_ID_PATTERN = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;
const CONNECTOR_ID_MAX_LENGTH = 63;

export const INVALID_CONNECTOR_ID = `Use lowercase letters, digits and hyphens only, starting and ending with a letter or digit, up to ${CONNECTOR_ID_MAX_LENGTH} characters.`;

/** Where an organization's access token is vaulted: the customer's own AWS
 * Secrets Manager or Google Secret Manager, in the same cloud as the
 * organization's connector. P0 stores where the secret is, never the token. */
export const DockerTokenVault = withoutAzureKeyVault(
  newVaultElement({ owner: "organization" })
);

/** Two items, and each artifact the install produces has one owner: the
 * deployed connector, its identity and P0's permission to invoke it belong to
 * `connector`; an organization's token secret, and the connector's read on it,
 * belong to `organization`. */
export const DockerComponents = InstallSpec({
  // One item per deployed connector, keyed by an id the customer chooses. A
  // connector serves every organization that points at it.
  //
  // The connector's identity isn't stored: its IAM role, or its service
  // account, is named after the connector (see `toConnectorIdentity`), as a
  // connector the customer deploys themselves must name it too.
  connector: ItemComponent({
    description:
      "Runs P0's Docker Hub connector in your AWS account or Google Cloud project. One connector can serve many organizations, so one is usually enough.",
    label: "Connector",
    maturity: "beta",
    schema: { hosting: ConnectorHosting },
    type: "string",
    validator: async (id) =>
      id.length <= CONNECTOR_ID_MAX_LENGTH && CONNECTOR_ID_PATTERN.test(id)
        ? undefined
        : INVALID_CONNECTOR_ID,
  }),
  // One item per Docker Hub organization, keyed by the organization's name.
  //
  // The name has no item validator here: the install framework's error for
  // one repeats the whole request, which may hold a token pasted in place of
  // its secret's name. The backend checks it instead (`DOCKER_ORG_PATTERN`).
  organization: ItemComponent({
    description:
      "A Docker Hub organization P0 can grant roles in, through one of your connectors",
    label: "Organization",
    maturity: "beta",
    // The connector, the vault and the token's secret are fixed when the item
    // is created: the organization's Terraform grants exactly that connector
    // read on exactly that secret. A rotated token goes into the same secret,
    // as a new version. The form asks for them in this order.
    schema: {
      connector: Element({
        description:
          "The connector that acts on this organization. P0 lists the Docker Hub connectors you've installed.",
        label: "Connector",
        step: "new",
        type: "dynamic",
      }),
      vault: DockerTokenVault,
      tokenSecretName: Element({
        description:
          "The name, not the ARN or the full resource name, of the secret that holds, or will hold, the organization access token. The organization's Terraform lets the connector read exactly this secret.",
        label: "Access token secret",
        step: "new",
        type: "string",
      }),
      allowedDomains: Element({
        description:
          "The email domains the connector acts for, as its DOMAIN_ALLOW_PATTERN read when the organization was last validated. To change them, change the connector's deployment.",
        label: "Allowed domains",
        type: "generated",
      }),
    },
    type: "string",
  }),
});
